import React, { useState, useEffect } from 'react';

const PartnershipTaxForm = ({ data, updateData, nextStep, prevStep, isLastStep, isProcessing }) => {
    const [formData, setFormData] = useState({
        // Income Fields
        grossSales: data?.grossSales || '',
        serviceIncome: data?.serviceIncome || '',
        otherBusinessIncome: data?.otherBusinessIncome || '',
        interestIncome: data?.interestIncome || '',
        rentalIncome: data?.rentalIncome || '',
        miscellaneousIncome: data?.miscellaneousIncome || '',

        // Direct Costs
        rawMaterialCost: data?.rawMaterialCost || '',
        purchaseCost: data?.purchaseCost || '',
        manufacturingCost: data?.manufacturingCost || '',
        labourCharges: data?.labourCharges || '',
        packagingCost: data?.packagingCost || '',
        transportationFreight: data?.transportationFreight || '',

        // Indirect Costs
        officeRent: data?.officeRent || '',
        electricityBill: data?.electricityBill || '',
        internetTelephone: data?.internetTelephone || '',
        marketingAdvertisement: data?.marketingAdvertisement || '',
        staffSalary: data?.staffSalary || '',
        professionalFees: data?.professionalFees || '',
        miscellaneousExpenses: data?.miscellaneousExpenses || '',

        // Financial & Other Costs
        directorSalary: data?.directorSalary || '',
        partnerRemuneration: data?.partnerRemuneration || data?.remunerationToPartners || '',
        interestOnCapital: data?.interestOnCapital || '',
        depreciation: data?.depreciation || '',
        loanInterest: data?.loanInterest || '',

        // Partner Details
        numberOfPartners: data?.numberOfPartners || '',
        profitSharingRatio: data?.profitSharingRatio || '',
        ...data
    });

    const [calculations, setCalculations] = useState({
        totalRevenue: 0,
        totalExpenses: 0,
        netProfit: 0,
        taxableIncome: 0,
        taxAmount: 0,
        cessAmount: 0,
        totalTaxPayable: 0
    });

    useEffect(() => {
        // Sum Income
        const totalRevenue = (
            parseFloat(formData.grossSales) || 0) +
            (parseFloat(formData.serviceIncome) || 0) +
            (parseFloat(formData.otherBusinessIncome) || 0) +
            (parseFloat(formData.interestIncome) || 0) +
            (parseFloat(formData.rentalIncome) || 0) +
            (parseFloat(formData.miscellaneousIncome) || 0
            );

        // Sum Expenses (Regular Business Expenses)
        const businessExpenses = (
            parseFloat(formData.rawMaterialCost) || 0) +
            (parseFloat(formData.purchaseCost) || 0) +
            (parseFloat(formData.manufacturingCost) || 0) +
            (parseFloat(formData.labourCharges) || 0) +
            (parseFloat(formData.packagingCost) || 0) +
            (parseFloat(formData.transportationFreight) || 0) +
            (parseFloat(formData.officeRent) || 0) +
            (parseFloat(formData.electricityBill) || 0) +
            (parseFloat(formData.internetTelephone) || 0) +
            (parseFloat(formData.marketingAdvertisement) || 0) +
            (parseFloat(formData.staffSalary) || 0) +
            (parseFloat(formData.professionalFees) || 0) +
            (parseFloat(formData.miscellaneousExpenses) || 0) +
            (parseFloat(formData.directorSalary) || 0) +
            (parseFloat(formData.depreciation) || 0) +
            (parseFloat(formData.loanInterest) || 0
            );

        // Net Profit as per P&L
        const netProfit = totalRevenue - businessExpenses;

        // Partner Deductions (for Tax Calculation)
        const remuneration = parseFloat(formData.partnerRemuneration) || 0;
        const interest = parseFloat(formData.interestOnCapital) || 0;

        // Taxable Income (Profit after partner remuneration and interest)
        const taxableIncome = Math.max(0, netProfit - remuneration - interest);

        // Tax Calculation (30% + 4% Cess)
        const taxAmount = Math.round(taxableIncome * 0.30);
        const cessAmount = Math.round(taxAmount * 0.04);
        const totalTaxPayable = taxAmount + cessAmount;

        setCalculations({
            totalRevenue,
            totalExpenses: businessExpenses,
            netProfit,
            taxableIncome,
            taxAmount,
            cessAmount,
            totalTaxPayable
        });
    }, [formData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        const updatedData = {
            ...formData,
            ...calculations,
            // Maintain compatibility with previous keys if needed
            totalAnnualRevenue: calculations.totalRevenue,
            totalExpenses: calculations.totalExpenses,
            remunerationToPartners: formData.partnerRemuneration
        };
        e.preventDefault();
        updateData(updatedData);
        nextStep(updatedData);
    };

    const formatCurrency = (val) => new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
    }).format(val || 0);

    return (
        <div className="form-step">
            <h2 className="step-title">Business Tax Details</h2>
            <p className="step-description">Detailed Profit & Loss Statement for Partnership Firm / LLP / Pvt Ltd</p>

            <div className="info-card">
                <div className="info-content">
                    <strong>Note:</strong> Tax is calculated at a flat 30% rate plus 4% Cess on the taxable income after Partner Remuneration and Interest.
                </div>
            </div>

            <form onSubmit={handleSubmit} className="diagnosis-form detail-form">

                {/* Section 1: Income Details */}
                <div className="form-section-card">
                    <div className="section-header-premium">
                        
                        <h3 className="section-heading-premium">Section 1: Income Details</h3>
                    </div>
                    <div className="grid-2-col">
                        <div className="form-group-premium">
                            <label>Gross Sales / Turnover</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input 
                                    type="number" 
                                    name="grossSales" 
                                    value={formData.grossSales} 
                                    onChange={handleChange} 
                                    placeholder="Annual Amount" 
                                    onWheel={(e) => {
                                        e.target.blur();
                                        e.stopPropagation();
                                    }}
                                    onKeyDown={(e) => {
                                        if (['e', 'E', '+', '-', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                />
                            </div>
                        </div>
                        <div className="form-group-premium">
                            <label>Service Income</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="serviceIncome" value={formData.serviceIncome} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                        <div className="form-group-premium">
                            <label>Other Business Income</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="otherBusinessIncome" value={formData.otherBusinessIncome} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                        <div className="form-group-premium">
                            <label>Interest Income</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="interestIncome" value={formData.interestIncome} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                        <div className="form-group-premium">
                            <label>Rental Income</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="rentalIncome" value={formData.rentalIncome} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                        <div className="form-group-premium">
                            <label>Miscellaneous Income</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="miscellaneousIncome" value={formData.miscellaneousIncome} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 2: Direct Costs (Production/Trading) */}
                <div className="form-section-card">
                    <div className="section-header-premium">
                        
                        <h3 className="section-heading-premium">Section 2: Direct Costs (Production/Trading)</h3>
                    </div>
                    <div className="grid-2-col">
                        <div className="form-group-premium">
                            <label>Raw Material Cost</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="rawMaterialCost" value={formData.rawMaterialCost} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                        <div className="form-group-premium">
                            <label>Purchase Cost</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="purchaseCost" value={formData.purchaseCost} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                        <div className="form-group-premium">
                            <label>Manufacturing Cost</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="manufacturingCost" value={formData.manufacturingCost} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                        <div className="form-group-premium">
                            <label>Labour Charges</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="labourCharges" value={formData.labourCharges} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                        <div className="form-group-premium">
                            <label>Packaging Cost</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="packagingCost" value={formData.packagingCost} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                        <div className="form-group-premium">
                            <label>Transportation / Freight</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="transportationFreight" value={formData.transportationFreight} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 3: Indirect Costs (Admin/Operations) */}
                <div className="form-section-card">
                    <div className="section-header-premium">
                        
                        <h3 className="section-heading-premium">Section 3: Indirect Costs (Admin/Operations)</h3>
                    </div>
                    <div className="grid-2-col">
                        <div className="form-group-premium">
                            <label>Office Rent</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="officeRent" value={formData.officeRent} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                        <div className="form-group-premium">
                            <label>Electricity Bill</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="electricityBill" value={formData.electricityBill} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                        <div className="form-group-premium">
                            <label>Internet & Telephone</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="internetTelephone" value={formData.internetTelephone} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                        <div className="form-group-premium">
                            <label>Marketing / Advertisement</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="marketingAdvertisement" value={formData.marketingAdvertisement} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                        <div className="form-group-premium">
                            <label>Staff Salary</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="staffSalary" value={formData.staffSalary} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                        <div className="form-group-premium">
                            <label>Professional Fees (CA, Legal)</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="professionalFees" value={formData.professionalFees} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                        <div className="form-group-premium">
                            <label>Miscellaneous Expenses</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="miscellaneousExpenses" value={formData.miscellaneousExpenses} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 4: Financial & Non-Cash Costs */}
                <div className="form-section-card">
                    <div className="section-header-premium">
                       
                        <h3 className="section-heading-premium">Section 4: Financial & Other Costs</h3>
                    </div>
                    <div className="grid-2-col">
                        <div className="form-group-premium">
                            <label>Director Salary (Pvt Ltd)</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="directorSalary" value={formData.directorSalary} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                        <div className="form-group-premium">
                            <label>Partner Remuneration (Partnership)</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="partnerRemuneration" value={formData.partnerRemuneration} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                        <div className="form-group-premium">
                            <label>Interest on Capital</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="interestOnCapital" value={formData.interestOnCapital} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                        <div className="form-group-premium">
                            <label>Depreciation</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="depreciation" value={formData.depreciation} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                        <div className="form-group-premium">
                            <label>Loan Interest</label>
                            <div className="input-with-icon-premium">
                                <span className="currency-symbol-premium">₹</span>
                                <input type="number" name="loanInterest" value={formData.loanInterest} onChange={handleChange} placeholder="Annual" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 5: Partner Details */}
                <div className="form-section-card secondary">
                    <div className="section-header-premium">
                        <div className="section-icon">👥</div>
                        <h3 className="section-heading-premium">Section 5: Partner Details</h3>
                    </div>
                    <div className="grid-2-col">
                        <div className="form-group-premium">
                            <label>Number of Partners</label>
                            <input type="number" name="numberOfPartners" value={formData.numberOfPartners} onChange={handleChange} placeholder="e.g. 2" />
                        </div>
                        <div className="form-group-premium">
                            <label>Profit Sharing Ratio (%)</label>
                            <div className="input-with-icon-premium right">
                                <input type="number" name="profitSharingRatio" value={formData.profitSharingRatio} onChange={handleChange} placeholder="Total %" />
                                <span className="unit-symbol-premium">%</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={() => { updateData(formData); prevStep(); }} className="btn-secondary">← Back</button>
                    <button type="submit" className="btn-primary" disabled={isProcessing}>
                        {isProcessing ? 'Processing...' : (isLastStep ? 'Generate Report →' : 'Next Step →')}
                    </button>
                </div>
            </form>

            <style jsx>{`
                .form-section-card {
                    background: #ffffff;
                    padding: 24px;
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                    margin-bottom: 24px;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                }
                .form-section-card.secondary {
                    background: #f8fafc;
                }
                .section-header-premium {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 20px;
                }
                .section-icon {
                    font-size: 1.5rem;
                }
                .section-heading-premium {
                    font-size: 1.15rem;
                    color: #1e293b;
                    font-weight: 700;
                    margin: 0;
                }
                .grid-2-col {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 20px;
                }
                .form-group-premium {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }
                .form-group-premium label {
                    font-size: 0.9rem;
                    font-weight: 600;
                    color: #475569;
                }
                .input-with-icon-premium {
                    position: relative;
                    display: flex;
                    align-items: center;
                }
                .input-with-icon-premium.right input {
                    padding-right: 35px;
                }
                .currency-symbol-premium {
                    position: absolute;
                    left: 12px;
                    color: #94a3b8;
                    font-weight: 500;
                    pointer-events: none;
                }
                .unit-symbol-premium {
                    position: absolute;
                    right: 12px;
                    color: #94a3b8;
                    font-weight: 500;
                }
                .form-group-premium input {
                    width: 100%;
                    padding: 12px 12px 12px 30px;
                    border: 1px solid #cbd5e1;
                    border-radius: 10px;
                    font-size: 1rem;
                    transition: all 0.2s;
                    background: #ffffff;
                }
                .form-group-premium input:focus {
                    outline: none;
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }
                @media (max-width: 640px) {
                    .grid-2-col {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
};

export default PartnershipTaxForm;
